/**
 * KenoRoundLookupTab
 * Admin → Keno → Round Lookup. Paste a Super Double-Up Keno round ID and get
 * the player's picks, the full 20-ball draw, the real match count and a
 * re-computed payout verification. Settles "it paid wrong" disputes instantly.
 * Backed by GET /api/admin/keno-super-double/round/{round_id}.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, CheckCircle2, AlertTriangle, Loader2, ListChecks } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KenoRoundLookupTab = () => {
    const [roundId, setRoundId] = useState('');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const [batchText, setBatchText] = useState('');
    const [batchLoading, setBatchLoading] = useState(false);
    const [batch, setBatch] = useState(null);

    const lookup = async () => {
        const id = roundId.trim();
        if (!id) { toast.error('Enter a round ID'); return; }
        setLoading(true);
        setData(null);
        try {
            const res = await axios.get(`${API}/admin/keno-super-double/round/${encodeURIComponent(id)}`, { withCredentials: true });
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.status === 404 ? 'Round not found' : (e.response?.data?.detail || 'Lookup failed'));
        } finally {
            setLoading(false);
        }
    };

    const matchSet = new Set(data?.matches || []);

    const runBatch = async () => {
        const text = batchText.trim();
        if (!text) { toast.error('Paste one or more round IDs'); return; }
        setBatchLoading(true);
        setBatch(null);
        try {
            const res = await axios.post(
                `${API}/admin/keno-super-double/verify-batch`,
                { round_ids: text },
                { withCredentials: true }
            );
            setBatch(res.data);
            if (res.data.mismatch_count > 0) toast.error(`${res.data.mismatch_count} round(s) flagged`);
            else toast.success('All rounds verified');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Batch verify failed');
        } finally {
            setBatchLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="keno-round-lookup-tab">
            <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Search className="w-5 h-5 text-[#4bd0ff]" /> Super Double-Up · Round Lookup
                </h2>
                <p className="text-sm text-white/50 mt-1">
                    Paste a round ID to verify exactly what was drawn, how many the player hit, and the correct payout.
                </p>
            </div>

            <div className="flex gap-2">
                <input
                    type="text"
                    value={roundId}
                    onChange={(e) => setRoundId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookup()}
                    placeholder="e.g. 0f58596f-7b8f-4cd9-95cf-d9efb01010f7"
                    className="flex-1 px-4 py-3 rounded-lg bg-[#121212] border border-white/10 text-white placeholder:text-white/30 focus:border-[#4bd0ff] focus:outline-none text-sm font-mono"
                    data-testid="keno-lookup-input"
                />
                <button
                    onClick={lookup}
                    disabled={loading}
                    className="px-6 py-3 rounded-lg bg-[#4bd0ff] text-black font-bold text-sm hover:bg-[#6dd8ff] disabled:opacity-50 flex items-center gap-2"
                    data-testid="keno-lookup-btn"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    LOOK UP
                </button>
            </div>

            {data && (
                <div className="space-y-5" data-testid="keno-lookup-result">
                    {/* Verification banner */}
                    <div
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${data.verified ? 'bg-green-500/10 border-green-500/40 text-green-400' : 'bg-red-500/10 border-red-500/40 text-red-400'}`}
                        data-testid="keno-lookup-verified"
                    >
                        {data.verified ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                        <div>
                            <div className="font-bold">
                                {data.verified ? 'PAYOUT VERIFIED — settlement matches the draw' : 'MISMATCH — stored payout does not match a re-computation'}
                            </div>
                            <div className="text-xs opacity-80">
                                Stored hits {data.stored_hits} · re-counted hits {data.hits} · stored payout {money(data.stored_final_payout)} · expected {money(data.expected_final_payout)}
                            </div>
                        </div>
                    </div>

                    {/* Meta */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { l: 'Player', v: data.user_email || data.user_id },
                            { l: 'Type', v: data.round_type },
                            { l: 'When', v: new Date(data.created_at).toLocaleString() },
                            { l: 'Spots', v: `${data.spots}` },
                            { l: 'Starting Wager', v: money(data.starting_wager) },
                            { l: 'Double Up', v: data.double_up_chosen ? 'YES' : 'NO' },
                            { l: 'Active Wager', v: money(data.active_wager) },
                            { l: 'Superball (20th)', v: `#${data.superball}${data.superball_hit ? ' — HIT ×4' : ' — no match'}` },
                        ].map((m) => (
                            <div key={m.l} className="bg-[#121212] border border-white/10 rounded-lg px-3 py-2">
                                <div className="text-[0.65rem] uppercase tracking-wider text-white/40">{m.l}</div>
                                <div className="text-sm text-white font-semibold truncate">{m.v}</div>
                            </div>
                        ))}
                    </div>

                    {/* Picks */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Player picks ({data.spots}) — gold = matched</div>
                        <div className="flex flex-wrap gap-2">
                            {data.selected_numbers.map((n) => (
                                <span
                                    key={n}
                                    className={`w-9 h-9 rounded-md flex items-center justify-center text-sm font-bold ${matchSet.has(n) ? 'bg-[#ffcc00] text-black' : 'bg-[#c81f1f] text-white'}`}
                                >{n}</span>
                            ))}
                        </div>
                    </div>

                    {/* Draw */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-white/40 mb-2">20 balls drawn — gold = one of the player's picks</div>
                        <div className="flex flex-wrap gap-2">
                            {data.drawn_numbers.map((n, i) => (
                                <span
                                    key={i}
                                    className={`w-9 h-9 rounded-md flex items-center justify-center text-sm font-bold border ${i === 19 ? 'ring-2 ring-[#4bd0ff]' : ''} ${matchSet.has(n) ? 'bg-[#ffcc00] text-black border-transparent' : 'bg-black text-white/70 border-white/15'}`}
                                    title={i === 19 ? 'Superball (20th ball)' : `Ball ${i + 1}`}
                                >{n}</span>
                            ))}
                        </div>
                    </div>

                    {/* Payout math */}
                    <div className="bg-[#121212] border border-white/10 rounded-lg p-4 space-y-2">
                        <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Payout math</div>
                        {[
                            { l: `Matched ${data.hits} of ${data.spots}`, v: data.matches.join(', ') || '—' },
                            { l: `Paytable multiplier (${data.hits}/${data.spots})`, v: `${data.paytable_multiplier}×` },
                            { l: 'Base pays (mult × active wager)', v: money(data.base_payout) },
                            { l: 'Superball ×4 applied', v: data.superball_hit ? `YES → ${money(data.base_payout * 4)}` : 'no' },
                            data.capped ? { l: 'Per-round cap applied', v: 'YES' } : null,
                            { l: 'Progressive jackpot', v: money(data.progressive_award) },
                        ].filter(Boolean).map((r) => (
                            <div key={r.l} className="flex justify-between text-sm">
                                <span className="text-white/60">{r.l}</span>
                                <span className="text-white font-semibold">{r.v}</span>
                            </div>
                        ))}
                        <div className="flex justify-between pt-2 border-t border-white/10 mt-2">
                            <span className="text-white font-bold">TOTAL PAID</span>
                            <span className="text-[#39ff14] font-bold text-lg">{money(data.total_paid)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Batch Dispute Check ---- */}
            <div className="pt-6 mt-2 border-t border-white/10 space-y-3" data-testid="keno-batch-section">
                <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <ListChecks className="w-5 h-5 text-[#4bd0ff]" /> Batch Dispute Check
                    </h3>
                    <p className="text-sm text-white/50 mt-1">
                        Paste many round IDs (one per line, or separated by spaces/commas). Any round whose stored payout doesn't match a fresh re-computation is flagged.
                    </p>
                </div>
                <textarea
                    value={batchText}
                    onChange={(e) => setBatchText(e.target.value)}
                    rows={4}
                    placeholder={"f7fa30d6-561c-47c8-8bc4-296e694e00cb\n0f58596f-7b8f-4cd9-95cf-d9efb01010f7"}
                    className="w-full px-4 py-3 rounded-lg bg-[#121212] border border-white/10 text-white placeholder:text-white/30 focus:border-[#4bd0ff] focus:outline-none text-sm font-mono resize-y"
                    data-testid="keno-batch-input"
                />
                <button
                    onClick={runBatch}
                    disabled={batchLoading}
                    className="px-6 py-3 rounded-lg bg-[#4bd0ff] text-black font-bold text-sm hover:bg-[#6dd8ff] disabled:opacity-50 flex items-center gap-2"
                    data-testid="keno-batch-btn"
                >
                    {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
                    VERIFY ALL
                </button>

                {batch && (
                    <div className="space-y-3" data-testid="keno-batch-result">
                        <div className="flex flex-wrap gap-3 text-sm">
                            <span className="px-3 py-1 rounded-full bg-white/5 text-white/70">Checked {batch.count}</span>
                            <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-400">Verified {batch.verified_count}</span>
                            <span className={`px-3 py-1 rounded-full ${batch.mismatch_count > 0 ? 'bg-red-500/15 text-red-400' : 'bg-white/5 text-white/40'}`}>
                                Flagged {batch.mismatch_count}
                            </span>
                        </div>
                        {batch.mismatch_count > 0 && (
                            <p className="text-xs text-yellow-400/80 flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                Note: the hit count is always authoritative. A payout "MISMATCH" on an <b className="mx-1">older</b> round usually just means the paytable was re-tuned since it was settled — not a settlement error. Open the round above to compare.
                            </p>
                        )}
                        <div className="overflow-hidden rounded-lg border border-white/10">
                            <table className="w-full text-sm">
                                <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-3 py-2">Round</th>
                                        <th className="text-left px-3 py-2">Status</th>
                                        <th className="text-right px-3 py-2">Hits</th>
                                        <th className="text-right px-3 py-2">Stored</th>
                                        <th className="text-right px-3 py-2">Expected</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {batch.results.map((r) => (
                                        <tr
                                            key={r.round_id}
                                            className={`border-t border-white/5 ${!r.verified ? 'bg-red-500/5' : ''}`}
                                            data-testid="keno-batch-row"
                                        >
                                            <td className="px-3 py-2 font-mono text-xs text-white/70">{r.round_id.slice(0, 8).toUpperCase()}</td>
                                            <td className="px-3 py-2">
                                                {!r.found ? (
                                                    <span className="text-yellow-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Not found</span>
                                                ) : r.verified ? (
                                                    <span className="text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>
                                                ) : (
                                                    <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> MISMATCH</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-white/80">{r.found ? `${r.hits}/${r.spots}` : '—'}</td>
                                            <td className="px-3 py-2 text-right text-white/80">{r.found ? money(r.stored_final_payout) : '—'}</td>
                                            <td className="px-3 py-2 text-right text-white/80">{r.found ? money(r.expected_final_payout) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KenoRoundLookupTab;
